import { Injectable } from '@angular/core';
import { UserDataService } from '@app/user/services/user-data.service';
import { PlatformService, Tracking } from '@panamax/app-state';
import { PanAppState } from '@panamax/app-state';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';
import { OrderStatus } from '@usf/ngrx-order';
import { OrderConfirmationVM } from '@order/models/order-confirmation-view-model';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { ModalService } from '../modal/modal.service';
import { ModalController } from '@ionic/angular';
import { MobileModalHeights } from '@shared/constants/modal-heights.enum';
import { UserReviewModalComponent as UserReviewModalComponent } from '@shared/components/user-review-modal/user-review-modal.component';
import { PlatformEnum } from '@panamax/app-state';
import { UserReviewActions } from '@shared/constants/user-review.enum';
import { UsabillaSdkService } from '../usabilla-sdk.service';
import { Store } from '@ngrx/store';
import { USER_REVIEW_TRACING_ACTIONS } from './user-review.actions';
import { Capacitor } from '@capacitor/core';
import { InAppReview } from '@capacitor-community/in-app-review';

@Injectable({
  providedIn: 'root',
})
export class UserReviewService {
  constructor(
    private platformService: PlatformService,
    private userDataService: UserDataService,
    private panAppState: PanAppState,
    private modalService: ModalService,
    private modalCtrl: ModalController,
    private usabillaSdkService: UsabillaSdkService,
    private store: Store,
  ) {}

  initiateAppReviewFlow(vm: OrderConfirmationVM) {
    combineLatest([
      this.panAppState.previousUrl$,
      this.panAppState.feature$([FEATURES.split_dynamic_app_reviews.name]),
    ])
      .pipe(take(1))
      .subscribe(([prevUrl, featureEnabled]) => {
        if (
          this.isEligibleOrderForAppReview(vm) &&
          featureEnabled &&
          prevUrl.endsWith(PATHS.ORDER) //avoids incrementing count when accessing confirmation page from a deep link
        ) {
          this.checkCanReview();
        }
      });
  }

  private checkCanReview() {
    this.userDataService
      .canRequestReview()
      .pipe(take(1))
      .subscribe(canRequestReviewResponse => {
        this.store.dispatch(
          USER_REVIEW_TRACING_ACTIONS.traceCanReviewResponse({
            tracking: this.generateTrackingMessage(
              JSON.stringify(canRequestReviewResponse),
            ),
          }),
        );
        if (canRequestReviewResponse?.canReview) {
          if (
            Capacitor.isNativePlatform() &&
            this.platformService.isTouch.value &&
            (this.platformService.platformType === PlatformEnum.mobile ||
              this.platformService.platformType === PlatformEnum.tablet)
          ) {
            this.initialRequestReviewModal();
          } else {
            console.log(
              '[UserReviewService] Skipping app review prompt: not a native device',
            );
          }
        }
      });
  }

  // Initial thumbs up/down modal
  private async initialRequestReviewModal() {
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      UserReviewModalComponent,
      {},
      'modal-desktop-size-md',
      null,
      null,
      MobileModalHeights.short,
    );
    const modal = await this.modalCtrl.create(this.modalService.modalOptions);

    await modal.present();
    modal.onDidDismiss().then(value => {
      const review = value?.data?.review;
      if (review) {
        this.handleReviewAction(review);
      } else {
        this.handleReviewAction(UserReviewActions.DISMISS);
      }
    });
  }

  private handleReviewAction(action: UserReviewActions) {
    this.userDataService
      .saveUserReview({ action })
      .pipe(take(1))
      .subscribe(saveUserReviewResponse => {
        this.store.dispatch(
          USER_REVIEW_TRACING_ACTIONS.traceSaveUserReviewRequest({
            tracking: this.generateTrackingMessage(
              JSON.stringify(saveUserReviewResponse?.message),
              action,
            ),
          }),
        );

        if (action === UserReviewActions.UP) {
          this.requestInAppReview(action);
        } else if (action === UserReviewActions.DOWN) {
          this.usabillaSdkService.loadSDKForm();
        }
      });
  }

  //App Store/Play Store review modal
  async requestInAppReview(reviewAction?: UserReviewActions) {
    if (!Capacitor.isPluginAvailable('InAppReview')) {
      console.log(
        '[UserReviewService] InAppReview plugin not available, skipping...',
      );
      return;
    }
    console.log(
      '[UserReviewService] Attempting to prompt for App/Playstore review',
    );
    try {
      await InAppReview.requestReview();
      console.log('[UserReviewService] Official app review was requested');
      this.store.dispatch(
        USER_REVIEW_TRACING_ACTIONS.traceRequestUserReview({
          tracking: this.generateTrackingMessage(undefined, reviewAction),
        }),
      );
    } catch (error: any) {
      console.error(
        '[UserReviewService] Error showing official app review prompt:',
        error,
      );
      this.store.dispatch(
        USER_REVIEW_TRACING_ACTIONS.traceRequestUserReviewError({
          tracking: this.generateTrackingMessage(error.message, reviewAction),
        }),
      );
    }
  }

  // Order must be an initial submit without order exceptions
  private isEligibleOrderForAppReview(vm: OrderConfirmationVM): boolean {
    const isInitialSubmit =
      vm?.masterOrderById?.orderHeader?.orderStatus === OrderStatus.DELETED;

    const isSubmittedStatus = (vm?.splittedOrders ?? []).some(
      order =>
        order.order?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
        order.order?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTED_CREDIT_HOLD,
    );

    const doesNotContainOrderWithExceptions = !(vm?.splittedOrders ?? []).some(
      order =>
        order.order?.orderHeader?.orderStatus ===
        OrderStatus.SUBMITTED_WITH_EXCEPTIONS,
    );

    return (
      isInitialSubmit && isSubmittedStatus && doesNotContainOrderWithExceptions
    );
  }

  private generateTrackingMessage(
    message?: string,
    reviewAction?: UserReviewActions,
  ): Tracking {
    let attributes: any = {};
    if (message) attributes.requestReviewMessage = message;
    if (reviewAction) attributes.reviewAction = reviewAction;
    if (Object.keys(attributes).length === 0) attributes = null;

    return {
      tracing: {
        data: {
          traceContext: 'requestReview',
          isEndOfTrace: true,
          isStartOfTrace: true,
          attributes: attributes,
        },
        transformFunc: genericTracingTransformer,
      },
    };
  }
}
